import { CONNECTORS } from './connectors';
import { getQueueStats, getWebhooks, type TaskQueueStats } from './async-tasks';

export interface AgentEnvelope {
  protocol: 'mediablox-agent/1.0';
  ts: number;
  request_id: string;
  status: 'ok' | 'error' | 'pending';
  action: string;
  payload: unknown;
  capabilities?: string[];
  next_actions?: string[];
  meta?: Record<string, unknown>;
}

export interface AgentCapabilityManifest {
  agent_id: string;
  version: string;
  protocol: string;
  description: string;
  supported_formats: string[];
  commands: AgentCommandSpec[];
  connectors: { id: string; name: string; category: string; status: string }[];
  webhooks: { id: string; url: string; active: boolean; events: string[] }[];
  queue_stats: TaskQueueStats;
  time_scales: { instant: string; async: string; long_running: string };
}

export interface AgentCommandSpec {
  name: string;
  description: string;
  params: { name: string; type: string; required: boolean; description: string }[];
  returns: string;
  time_scale: string;
  example: string;
}

const COMMAND_SPECS: AgentCommandSpec[] = [
  {
    name: 'status',
    description: 'Return corpus statistics and system state',
    params: [],
    returns: '{ total, in_review, approved, duration_minutes, jobs_active }',
    time_scale: 'instant',
    example: 'status',
  },
  {
    name: 'select',
    description: 'Target tracks by filter (status, id prefix, or "all")',
    params: [{ name: 'filter', type: 'string', required: true, description: 'Filter: "review", "draft", "approved", "all", or track ID prefix' }],
    returns: '{ count, ids }',
    time_scale: 'instant',
    example: 'select review',
  },
  {
    name: 'approve',
    description: 'Move targeted tracks from review to approved status',
    params: [],
    returns: '{ affected, new_status }',
    time_scale: 'instant',
    example: 'approve',
  },
  {
    name: 'publish',
    description: 'Publish targeted tracks (irreversible)',
    params: [],
    returns: '{ affected, new_status, task_id }',
    time_scale: 'async',
    example: 'publish',
  },
  {
    name: 'discover',
    description: 'List all registered API connectors grouped by category',
    params: [],
    returns: '{ connectors: [{ id, name, category, status }] }',
    time_scale: 'instant',
    example: 'discover',
  },
  {
    name: 'describe',
    description: 'Get full self-documentation for a connector',
    params: [{ name: 'id', type: 'string', required: true, description: 'Connector ID or partial name match' }],
    returns: '{ id, name, endpoints, auth, rate_limits }',
    time_scale: 'instant',
    example: 'describe spotify-catalog',
  },
  {
    name: 'queue',
    description: 'Show async task queue state and recent tasks',
    params: [],
    returns: '{ stats, recent_tasks, webhooks }',
    time_scale: 'instant',
    example: 'queue',
  },
  {
    name: 'webhooks',
    description: 'List registered webhook endpoints',
    params: [],
    returns: '{ webhooks: [{ id, url, active, events }] }',
    time_scale: 'instant',
    example: 'webhooks',
  },
  {
    name: 'layer',
    description: 'Switch capability layer (basic, advanced, expert)',
    params: [{ name: 'level', type: 'string', required: false, description: 'Target layer; omit to cycle' }],
    returns: '{ layer, available_ops }',
    time_scale: 'instant',
    example: 'layer expert',
  },
  {
    name: 'connect',
    description: 'Initiate connection setup for a connector',
    params: [{ name: 'id', type: 'string', required: true, description: 'Connector ID' }],
    returns: '{ connector_id, auth_method, docs_url, status }',
    time_scale: 'instant',
    example: 'connect spotify-catalog',
  },
];

export function getManifest(): AgentCapabilityManifest {
  return {
    agent_id: 'mediablox-pipes',
    version: '1.0.0',
    protocol: 'mediablox-agent/1.0',
    description: 'Music catalog management agent with multi-tier response formats, async task execution, and self-documenting API connectors. Designed for human operators and AI orchestration agents alike.',
    supported_formats: ['text/plain', 'application/json', 'text/minimal'],
    commands: COMMAND_SPECS,
    connectors: CONNECTORS.map(c => ({ id: c.id, name: c.name, category: c.category, status: c.status })),
    webhooks: getWebhooks().map(w => ({ id: w.id, url: w.url, active: w.active, events: w.events })),
    queue_stats: getQueueStats(),
    time_scales: { instant: '<1s', async: '15-60s', long_running: '2-10min' },
  };
}

export function envelope(action: string, payload: unknown, opts?: { status?: AgentEnvelope['status']; next?: string[]; meta?: Record<string, unknown> }): AgentEnvelope {
  return {
    protocol: 'mediablox-agent/1.0',
    ts: Date.now(),
    request_id: crypto.randomUUID().slice(0, 12),
    status: opts?.status ?? 'ok',
    action,
    payload,
    next_actions: opts?.next,
    meta: opts?.meta,
  };
}

export function formatForAgent(env: AgentEnvelope, format: 'json' | 'text' | 'minimal'): string {
  switch (format) {
    case 'json':
      return JSON.stringify(env, null, 2);
    case 'minimal':
      return `${env.status}:${env.action}:${typeof env.payload === 'string' ? env.payload : JSON.stringify(env.payload)}`;
    case 'text':
    default: {
      const lines = [`[${env.status.toUpperCase()}] ${env.action}`];
      if (typeof env.payload === 'string') {
        lines.push(env.payload);
      } else {
        lines.push(JSON.stringify(env.payload, null, 2));
      }
      if (env.next_actions?.length) {
        lines.push(`Next: ${env.next_actions.join(', ')}`);
      }
      return lines.join('\n');
    }
  }
}

export function describeManifest(): string {
  const m = getManifest();
  const cmdList = m.commands.map(c => `  ${c.name} [${c.time_scale}] - ${c.description}`).join('\n');
  return [
    `=== Agent Manifest: ${m.agent_id} v${m.version} ===`,
    `Protocol: ${m.protocol}`,
    `Formats: ${m.supported_formats.join(', ')}`,
    '',
    m.description,
    '',
    'Commands:',
    cmdList,
    '',
    `Connectors: ${m.connectors.length} registered`,
    `Webhooks: ${m.webhooks.filter(w => w.active).length} active`,
    `Queue: ${m.queue_stats.processing} processing, ${m.queue_stats.queued} queued`,
    '',
    'For JSON manifest: "manifest --json"',
  ].join('\n');
}
